// filepath: /Users/gero/Desktop/GitLab/servgest4/1/gestyFE/src/components/driver/dashboard/DriverCar.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ClockIcon,
  TruckIcon,
  FunnelIcon,
  WrenchIcon,
  BanknotesIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import CarDetailsModal from '../modals/CarDetailsModal';
import HistoryModal from '../modals/HistoryModal';
import ExpensesModal from '../modals/ExpensesModal';
import axios from 'axios';
import { auth } from '../../../firebase';
import { useTranslation } from 'react-i18next';
import { useCarContext } from '../../../contexts/CarContext';

const DriverCar = () => {
  const { t } = useTranslation();
  const { cars, loading, fetchCars, setCars } = useCarContext(); // Use context instead of local state
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCars, setFilteredCars] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOption, setSortOption] = useState('model'); // Default sort by model
  const [favorites, setFavorites] = useState([]);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [isExpensesModalOpen, setIsExpensesModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Modal states
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);

  // Track scrolling for header effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Function to open details modal
  const openDetailsModal = (car) => {
    // Ensure owner property is preserved from the original cars data
    const carWithOwner = { ...car };
    // If the car doesn't have owner property, we need to get it from the original API data
    // This is a temporary fix until the CarContext preserves all properties
    setSelectedCar(carWithOwner);
    setIsDetailsModalOpen(true);
  };

  // Function to open history modal
  const openHistoryModal = (car) => {
    setSelectedCar(car);
    setIsHistoryModalOpen(true);
  };

  // Function to open expenses modal
  const openExpensesModal = (car) => {
    setSelectedCar(car);
    setIsExpensesModalOpen(true);
  };

  // Filter and sort cars
  useEffect(() => {
    let result = [...cars];

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(car => car.status.toLowerCase() === statusFilter.toLowerCase());
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(car =>
        car.plate.toLowerCase().includes(query) ||
        car.make.toLowerCase().includes(query) ||
        car.model.toLowerCase().includes(query) ||
        car.color.toLowerCase().includes(query) ||
        car.fuelType.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (sortOption) {
        case 'model':
          return `${a.make} ${a.model}`.localeCompare(`${b.make} ${b.model}`);
        case 'year':
          return b.year - a.year;
        case 'plate':
          return a.plate.localeCompare(b.plate);
        case 'mileage':
          return a.mileage - b.mileage;
        case 'itp':
          return new Date(a.ITP) - new Date(b.ITP);
        case 'favorite':
          const aFav = favorites.includes(a.id) ? 0 : 1;
          const bFav = favorites.includes(b.id) ? 0 : 1;
          return aFav - bFav;
        default:
          return 0;
      }
    });

    setFilteredCars(result);
  }, [searchQuery, cars, statusFilter, sortOption, favorites]);

  // // Get status indicator color (dot)
  // const getStatusIndicator = (status) => {
  //   switch (status?.toLowerCase()) {
  //     case 'activ': return 'bg-emerald-500';
  //     case 'in service': return 'bg-amber-500';
  //     case 'inactiv': return 'bg-rose-500';
  //     default: return 'bg-gray-400';
  //   }
  // };

  // Get status text
  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'activ': return 'Activ';
      case 'in service': return 'În Service';
      case 'inactiv': return 'Inactiv';
      default: return status || 'Necunoscut';
    }
  };

  // Format date function (consistent with DriverHistory)
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('ro-RO', options);
    } catch {
      return dateString; // Fallback if parsing fails
    }
  };

  // Check if ITP is expired or nearing expiration (e.g., within 30 days)
  const getITPStatus = (itpDate) => {
    if (!itpDate) return { expired: false, nearing: false };
    const today = new Date();
    const expiryDate = new Date(itpDate);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      expired: diffDays < 0,
      nearing: diffDays >= 0 && diffDays <= 30, // Example: Nearing if 30 days or less
    };
  };

  // Check if service is due soon (within 1000 km of next service)
  const getServiceStatus = (car) => {
    if (!car.mileage || !car.nextServiceKm) return { due: false, soon: false };
    const kmUntilService = car.nextServiceKm - car.mileage;

    return {
      due: kmUntilService <= 0,
      soon: kmUntilService > 0 && kmUntilService <= 1000
    };
  };

  // Check if insurance is expired or nearing expiration
  const getInsuranceStatus = (insuranceDate) => {
    if (!insuranceDate) return { expired: false, nearing: false };
    const today = new Date();
    const expiryDate = new Date(insuranceDate);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      expired: diffDays < 0,
      nearing: diffDays >= 0 && diffDays <= 30, // Nearing if 30 days or less
    };
  };

  // Calculate total expenses for a car
  const calculateTotalExpenses = (expenses) => {
    if (!expenses || !expenses.length) return 0;
    return expenses.reduce((total, expense) => total + expense.amount, 0);
  };

  // Function to delete a car
  const handleDeleteCar = async (car) => {
    if (!window.confirm(t('vehicles.confirmDelete', { plate: car.plate }))) return;

    try {
      const idToken = await auth.currentUser?.getIdToken();
      await axios.delete('https://api.servicegest.ro/driver/car/delete', {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        params: {
          registrationNumber: car.plate,
        },
      });
      setCars(prev => prev.filter(c => c.id !== car.id));
      alert(t('vehicles.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting car:', error);
      alert(t('vehicles.deleteError'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 ">
        {/* Header */}
        <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <TruckIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                {t('vehicles.yourCars')}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t('driverCar.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className={`pt-2 pb-4 px-2 ${scrolled
          ? 'bg-white dark:bg-slate-800 rounded-xl'
          : 'bg-white dark:bg-slate-800 rounded-xl'
          }`}>
          <div className="w-full mx-auto relative pt-2">
            <div className="flex flex-row gap-4">
              {/* Search Bar */}
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('vehicles.searchPlaceholder')}
                  className="w-full pl-12 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-700 dark:text-white sm:text-sm"
                />
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
                </div>
                {searchQuery && (
                  <button
                    className="absolute inset-y-0  right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-gray-300"
                    onClick={() => setSearchQuery('')}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* Filter/Sort Controls */}
              <div className="flex gap-2">
                <div className="relative">
                  <button
                    onClick={() => setShowFilterMenu(!showFilterMenu)}
                    className="inline-flex items-center py-2 h-full px-3 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-gray-600"
                  >
                    <FunnelIcon className="h-4 w-4 mr-1" />
                    {t('vehicles.filter')}
                  </button>

                  {showFilterMenu && (
                    <div className="absolute right-0 mt-2 w-60 bg-white dark:bg-slate-800 rounded-md shadow-lg z-10 border border-slate-200 dark:border-slate-700">
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">{t('common.status')}</h4>
                        <div className="space-y-1">
                          <button
                            onClick={() => {
                              setStatusFilter('all');
                              setShowFilterMenu(false);
                            }}
                            className={`w-full text-left px-2 py-1 rounded-md text-sm ${statusFilter === 'all' ?
                              'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' :
                              'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                          >
                            {t('vehicles.filters.all')}
                          </button>
                          <button
                            onClick={() => {
                              setStatusFilter('activ');
                              setShowFilterMenu(false);
                            }}
                            className={`w-full text-left px-2 py-1 rounded-md text-sm ${statusFilter === 'activ' ?
                              'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' :
                              'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                          >
                            {t('vehicles.filters.active')}
                          </button>
                          <button
                            onClick={() => {
                              setStatusFilter('in service');
                              setShowFilterMenu(false);
                            }}
                            className={`w-full text-left px-2 py-1 rounded-md text-sm ${statusFilter === 'in service' ?
                              'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300' :
                              'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                          >
                            {t('vehicles.filters.inService')}
                          </button>
                          <button
                            onClick={() => {
                              setStatusFilter('inactiv');
                              setShowFilterMenu(false);
                            }}
                            className={`w-full text-left px-2 py-1 rounded-md text-sm ${statusFilter === 'inactiv' ?
                              'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' :
                              'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                          >
                            {t('vehicles.filters.inactive')}
                          </button>
                        </div>

                        <h4 className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 mt-4">{t('vehicles.sortBy')}</h4>
                        <div className="space-y-1">
                          <button
                            onClick={() => {
                              setSortOption('model');
                              setShowFilterMenu(false);
                            }}
                            className={`w-full text-left px-2 py-1 rounded-md text-sm ${sortOption === 'model' ?
                              'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' :
                              'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                          >
                            {t('vehicles.sortOptions.model')}
                          </button>
                          <button
                            onClick={() => {
                              setSortOption('year');
                              setShowFilterMenu(false);
                            }}
                            className={`w-full text-left px-2 py-1 rounded-md text-sm ${sortOption === 'year' ?
                              'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' :
                              'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                          >
                            {t('vehicles.sortOptions.year')}
                          </button>
                          <button
                            onClick={() => {
                              setSortOption('plate');
                              setShowFilterMenu(false);
                            }}
                            className={`w-full text-left px-2 py-1 rounded-md text-sm ${sortOption === 'plate' ?
                              'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' :
                              'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                          >
                            {t('vehicles.sortOptions.plate')}
                          </button>
                          <button
                            onClick={() => {
                              setSortOption('mileage');
                              setShowFilterMenu(false);
                            }}
                            className={`w-full text-left px-2 py-1 rounded-md text-sm ${sortOption === 'mileage' ?
                              'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' :
                              'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                          >
                            {t('vehicles.sortOptions.mileage')}
                          </button>
                          <button
                            onClick={() => {
                              setSortOption('itp');
                              setShowFilterMenu(false);
                            }}
                            className={`w-full text-left px-2 py-1 rounded-md text-sm ${sortOption === 'itp' ?
                              'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' :
                              'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                          >
                            {t('vehicles.sortOptions.itp')}
                          </button>
                          {/* <button 
                          onClick={() => {
                            setSortOption('favorite');
                            setShowFilterMenu(false);
                          }}
                          className={`w-full text-left px-2 py-1 rounded-md text-sm ${sortOption === 'favorite' ? 
                            'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 
                            'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                        >
                          Favorite
                        </button> */}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Active Filter Display */}
          {(statusFilter !== 'all' || sortOption !== 'model') && (
            <div className="px-6 pb-4 flex items-center flex-wrap gap-2">
              <span className="text-sm text-slate-500 dark:text-slate-400">{t('vehicles.activeFilters')}:</span>

              {statusFilter !== 'all' && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusFilter === 'activ' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                  statusFilter === 'in service' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                  }`}>
                  {t('common.status')}: {getStatusText(statusFilter)}
                  <button
                    onClick={() => setStatusFilter('all')}
                    className="ml-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}

              {sortOption !== 'model' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-xs font-medium">
                  {t('vehicles.sortingBy')}: {
                    sortOption === 'year' ? t('vehicles.sortOptions.year') :
                      sortOption === 'plate' ? t('vehicles.sortOptions.plate') :
                        sortOption === 'mileage' ? t('vehicles.sortOptions.mileage') :
                          sortOption === 'itp' ? t('vehicles.sortOptions.itp') :
                            sortOption === 'favorite' ? 'Favorite' : t('vehicles.sortOptions.model')
                  }
                  <button
                    onClick={() => setSortOption('model')}
                    className="ml-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Results status when filtering */}
          {searchQuery.trim() && (
            <div className="px-4 pb-4 text-center text-sm text-slate-500 dark:text-slate-400">
              {filteredCars.length === 0 ? (
                <p>{t('vehicles.noCarsFoundForSearch', { query: searchQuery })}</p>
              ) : (
                <p>{t('vehicles.carsFoundForSearch', { count: filteredCars.length, query: searchQuery })}</p>
              )}
            </div>
          )}

          {/* Car Grid & Table */}
          <div className="py-4 pb-6">
            {/* Card view for mobile and tablet */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:hidden ">
              <AnimatePresence>
                {filteredCars.map((car) => {
                  const itpStatus = getITPStatus(car.ITP);
                  const serviceStatus = getServiceStatus(car);
                  const insuranceStatus = getInsuranceStatus(car.insuranceExpiry);

                  return (
                    <motion.div
                      key={car.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      whileHover={{ y: -2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                      className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col"
                    >
                      {/* Card Content */}
                      <div className="p-2 flex-grow rounded-lg">
                        <div className="px-2 flex justify-between items-start mb-3">
                          {/* Car Make/Model */}
                          <div>
                            <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white">{car.make} {car.model}</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">{car.year} - {car.engineType} - {car.carDetails.enginePower} CP</p>
                          </div>
                        </div>

                        {/* Details Section */}
                        <div className="space-y-2.5 text-sm">
                          {/* Plate & VIN Section */}
                          <div className="bg-purple-50 dark:bg-purple-700/50 p-2 rounded-md border border-purple-200 dark:border-purple-700 space-y-2">
                            {/* License Plate */}
                            <div className="flex items-center text-slate-700 dark:text-slate-300">
                              <TruckIcon className="h-4 w-4 mr-2 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                              <span className="font-medium mr-1">{t('vehicles.plateNumberLabel')}:</span>
                              <span className="font-mono px-1.5 py-0.5 rounded text-slate-800 dark:text-slate-200">
                                {searchQuery && car.plate.toLowerCase().includes(searchQuery.toLowerCase()) ? (
                                  highlightMatch(car.plate, searchQuery)
                                ) : (
                                  car.plate
                                )}
                              </span>
                            </div>
                            <div className="flex items-center text-slate-700 dark:text-slate-300">
                              <TruckIcon className="h-4 w-4 mr-2 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                              <span className="font-medium mr-1">{t('vehicles.vinLabel')}:</span>
                              <span className="font-mono  px-1.5 py-0.5 rounded text-slate-800 dark:text-slate-200">
                                {searchQuery && car.vin.toLowerCase().includes(searchQuery.toLowerCase()) ? (
                                  highlightMatch(car.vin, searchQuery)
                                ) : (
                                  car.vin
                                )}
                              </span>
                            </div>
                          </div>
                          {/* Mileage */}
                          <div className="flex items-center text-slate-700 p-1.5 dark:text-slate-300">
                            <ClockIcon className="h-4 w-4 mr-2 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                            <span className="font-medium mr-1">{t('vehicles.mileageLabel')}:</span>
                            <span className="font-semibold">{car.mileage.toLocaleString()} km</span>

                            {serviceStatus.due && (
                              <span className="ml-2 px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs rounded-md font-medium">
                                {t('vehicles.serviceNeeded')}
                              </span>
                            )}
                            {serviceStatus.soon && !serviceStatus.due && (
                              <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs rounded-md font-medium">
                                {t('vehicles.serviceSoon')}
                              </span>
                            )}
                          </div>

                          {/* ITP Expiration */}
                          <div className={`flex items-center rounded p-1.5 ${itpStatus.expired ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' :
                            itpStatus.nearing ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300' :
                              'text-slate-700 dark:text-slate-300'
                            }`}>
                            <ClockIcon className={`h-4 w-4 mr-2 flex-shrink-0 ${itpStatus.expired ? 'text-red-500' : itpStatus.nearing ? 'text-yellow-500' : 'text-slate-400 dark:text-slate-500'
                              }`} />
                            <span className="font-medium mr-1">{t('vehicles.itpExpiresLabel')}:</span>
                            <span className="font-semibold">{formatDate(car.ITP)}</span>
                            {itpStatus.expired && <span className="ml-2 text-xs font-bold">({t('vehicles.expired')})</span>}
                            {itpStatus.nearing && !itpStatus.expired && <span className="ml-2 text-xs font-bold">({t('vehicles.expiringSoon')})</span>}
                          </div>

                          {/* Insurance Status */}
                          {/* <div className={`flex items-center rounded p-1.5 ${
                        insuranceStatus.expired ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' :
                        insuranceStatus.nearing ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300' :
                        'text-slate-700 dark:text-slate-300'
                      }`}>
                        <DocumentTextIcon className={`h-4 w-4 mr-2 flex-shrink-0 ${
                          insuranceStatus.expired ? 'text-red-500' : 
                          insuranceStatus.nearing ? 'text-yellow-500' : 
                          'text-slate-400 dark:text-slate-500'
                        }`} />
                        <span className="font-medium mr-1">{t('vehicles.insuranceLabel')}:</span>
                        <span className="font-semibold">{formatDate(car.insuranceExpiry)}</span>
                        {insuranceStatus.expired && <span className="ml-2 text-xs font-bold">({t('vehicles.expiredFeminine')})</span>}
                        {insuranceStatus.nearing && !insuranceStatus.expired && <span className="ml-2 text-xs font-bold">({t('vehicles.expiringSoon')})</span>}
                      </div> */}

                          {/* Last Service Info */}
                          {/* <div className="flex items-center text-slate-700 dark:text-slate-300 rounded p-1.5">
                            <WrenchIcon className="h-4 w-4 mr-2 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                            <span className="font-medium mr-1">{t('vehicles.lastServiceLabel')}:</span>
                            <span className="font-semibold">{formatDate(car.lastService)}</span>
                          </div> */}

                          {/* Total Expenses */}
                          {/* <div className="flex items-center text-slate-700 dark:text-slate-300 rounded p-1.5 bg-slate-50 dark:bg-slate-700/50">
                            <BanknotesIcon className="h-4 w-4 mr-2 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                            <span className="font-medium mr-1">{t('vehicles.totalExpensesLabel')}:</span>
                            <span className="font-semibold text-purple-600 dark:text-purple-400">{calculateTotalExpenses(car.expenses)} RON</span>
                          </div> */}
                        </div>
                      </div>

                      {/* Actions Footer */}
                      <div className="p-3 flex flex-col gap-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                        <div className="flex flex-row gap-2">
                          <button
                            onClick={() => openDetailsModal(car)}
                            className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800">
                            <InformationCircleIcon className="h-5 w-5 mr-1.5" />
                            {t('common.details')}
                          </button>
                          <button
                            onClick={() => openHistoryModal(car)}
                            className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-xs sm:text-sm font-medium rounded-md text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-gray-600">
                            <DocumentTextIcon className="h-5 w-5 mr-1.5" />
                            {t('quickActions.history')}
                          </button>
                        </div>
                        <div className="flex flex-row gap-2">
                          {/* <button
                            onClick={() => openExpensesModal(car)}
                            className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-xs sm:text-sm font-medium rounded-md text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-gray-600">
                            <BanknotesIcon className="h-5 w-5 mr-1.5" />
                            {t('vehicles.manageExpenses')}
                          </button> */}
                          <button
                            onClick={() => handleDeleteCar(car)}
                            className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-800">
                            <TrashIcon className="h-5 w-5 mr-1.5" />
                            {t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {/* Table view for desktop */}
            <div className="hidden lg:block bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">{t('vehicles.table.vehicle')}</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">{t('vehicles.plateNumberLabel')}</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">{t('vehicles.vinLabel')}</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">{t('vehicles.table.mileage')}</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">{t('vehicles.table.documents')}</th>
                    {/* <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">{t('vehicles.table.expenses')}</th> */}
                    <th scope="col" className="relative px-4 py-3">
                      <span className="sr-only">{t('vehicles.table.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  <AnimatePresence>
                    {filteredCars.map((car) => {
                      const itpStatus = getITPStatus(car.ITP);
                      const serviceStatus = getServiceStatus(car);
                      const insuranceStatus = getInsuranceStatus(car.insuranceExpiry);

                      return (
                        <motion.tr
                          key={car.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          layout
                          className="hover:bg-slate-50 dark:hover:bg-gray-700/50 overflow-auto"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm font-semibold text-slate-900 dark:text-white">{car.make} {car.model}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">{car.year} - {car.engineType}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className="font-mono px-1.5 py-0.5 rounded text-slate-800 dark:text-slate-200 text-xs">
                              {car.plate}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className="font-mono text-slate-500 dark:text-slate-400 text-xs">{car.vin}</span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">
                            <div className="font-semibold">{car.mileage.toLocaleString()} km</div>
                            {serviceStatus.due && (
                              <div className="text-xs text-rose-600 dark:text-red-400 mt-1">{t('vehicles.serviceNeeded')}</div>
                            )}
                            {serviceStatus.soon && !serviceStatus.due && (
                              <div className="text-xs text-amber-600 dark:text-yellow-400 mt-1">{t('vehicles.serviceSoon')}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <div className={`flex items-center ${itpStatus.expired ? 'text-rose-600 dark:text-red-400' : itpStatus.nearing ? 'text-amber-600 dark:text-yellow-400' : 'text-slate-700 dark:text-slate-300'}`}>
                              <span className="font-medium">ITP:</span>
                              <span className="ml-1 font-semibold">{formatDate(car.ITP)}</span>
                            </div>
                            {/* <div className={`flex items-center mt-1 ${insuranceStatus.expired ? 'text-rose-600 dark:text-red-400' : insuranceStatus.nearing ? 'text-amber-600 dark:text-yellow-400' : 'text-slate-700 dark:text-slate-300'}`}>
                          <span className="font-medium">{t('vehicles.insuranceLabelShort')}:</span>
                          <span className="ml-1 font-semibold">{formatDate(car.insuranceExpiry)}</span>
                        </div> */}
                          </td>
                          {/* <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-purple-600 dark:text-purple-400">
                            {calculateTotalExpenses(car.expenses)} RON
                          </td> */}
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <button onClick={() => openDetailsModal(car)} className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 p-1 rounded-md hover:bg-slate-100 dark:hover:bg-gray-700">
                                <InformationCircleIcon className="h-5 w-5" />
                                <span className="text-xs font-semibold">{t('common.details')}</span>
                              </button>
                              <button onClick={() => openHistoryModal(car)} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-gray-200 p-1 rounded-md hover:bg-slate-100 dark:hover:bg-gray-700">
                                <DocumentTextIcon className="h-5 w-5" />
                                <span className="text-xs font-semibold">{t('quickActions.history')}</span>
                              </button>
                              {/* <button onClick={() => openExpensesModal(car)} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-gray-200 p-1 rounded-md hover:bg-slate-100 dark:hover:bg-gray-700">
                                <BanknotesIcon className="h-5 w-5" />
                                <span className="text-xs font-semibold">{t('quickActions.expenses')}</span>
                              </button> */}
                              <button onClick={() => handleDeleteCar(car)} className="inline-flex items-center gap-1 text-rose-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 rounded-md hover:bg-slate-100 dark:hover:bg-gray-700">
                                <TrashIcon className="h-5 w-5" />
                                <span className="text-xs font-semibold">{t('common.delete')}</span>
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>

          {/* No results messages */}
          {filteredCars.length === 0 && !searchQuery.trim() && (
            <div className="px-6 pb-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-10 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700"
              >
                <TruckIcon className="mx-auto h-10 w-10 text-slate-400 dark:text-slate-500 mb-3" />
                <h3 className="text-lg font-medium text-slate-900 dark:text-gray-100">{t('vehicles.noCarsAddedTitle')}</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('vehicles.noCarsAddedBody')}</p>
              </motion.div>
            </div>
          )}
          {filteredCars.length === 0 && searchQuery.trim() && (
            <div className="px-6 pb-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-10 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700"
              >
                <MagnifyingGlassIcon className="mx-auto h-10 w-10 text-slate-400 dark:text-slate-500 mb-3" />
                <h3 className="text-lg font-medium text-slate-900 dark:text-gray-100">{t('vehicles.noCarsFoundTitle')}</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('vehicles.noCarsFoundBody', { query: searchQuery })}</p>
              </motion.div>
            </div>
          )}
        </div>

        {/* Modals */}
        {/* 
        CarDetailsModal receives selectedCar with the following fields from fetch:
        - id: VIN or index (string)
        - make: car.make (string)
        - model: car.type (string)
        - status: (string, default '-')
        - year: car.essentialData?.productionYear (string/number)
        - plate: car.registrationNumber (string)
        - color: (string, default '-')
        - ITP: (string, default '')
        - mileage: (number, default 0)
        - lastService: (string, default '')
        - nextServiceKm: (number, default 0)
        - engineType: car.essentialData?.engineType (string)
        - enginePower: car.essentialData?.enginePower (string/number)
        - insuranceExpiry: (string, default '')
        - vignette: (string, default '')
        - expenses: (array, default [])
        - appointments: (array, from car.appointments)
      */}
        <CarDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          selectedCar={selectedCar}
          onCarsChanged={fetchCars}
        />
        <HistoryModal
          isOpen={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
          selectedCar={selectedCar}
        />
        <ExpensesModal
          isOpen={isExpensesModalOpen}
          onClose={() => setIsExpensesModalOpen(false)}
          selectedCar={selectedCar}
        />
      </div>
    </div>
  );
};

// Helper function to highlight matched text
const highlightMatch = (text, query) => {
  if (!query) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) return text;

  return (
    <>
      {text.substring(0, index)}
      <span className="bg-yellow-200 dark:bg-yellow-700 px-0.5">
        {text.substring(index, index + query.length)}
      </span>
      {text.substring(index + query.length)}
    </>
  );
};

export default DriverCar;
